/**
 * Design: Forecast Chip / Forecast Meter / Forecast Factor / Tension Gauge / Projection Bar
 * (screenshots/26-legislation-detail.png, 27-vote-forecast-model.png, 35, 41 cards 1–5). Pure DOM, no chart lib.
 * Blue/orange are reserved for predictions; recorded votes keep the green/red YEA/NAY chips.
 * Values only animate when they change after first paint (motion 11); reduced motion swaps them.
 */
import { useLayoutEffect, useRef, type CSSProperties } from "react";
import { MoveHorizontal, TrendingDown, TrendingUp } from "lucide-react";
import { tensionLabel, type Forecast, type ForecastFactor, type ForecastLabel } from "./forecast";
import type { Perspective } from "../legislation/logic";
import { easing, prefersReducedMotion, recipes } from "../motion";

export type { Perspective };
export const pct = (p: number) => Math.round(p * 100);
const way = (v: Perspective) => (v === "yes" ? "yes" : `${v} way`);

export const LABEL_ICON = { likely: TrendingUp, tossup: MoveHorizontal, unlikely: TrendingDown } as const;

/** "Likely their way · 66%", "Toss-up · 52%", "Unlikely your way · 18%", "Likely yes · 92%". */
export function chipText(label: ForecastLabel, perspective: Perspective) {
  return label === "likely" ? `Likely ${way(perspective)}` : label === "unlikely" ? `Unlikely ${way(perspective)}` : "Toss-up";
}

/** Pass a forecast (`f`, with `showPct` as in the starter kit), or a `label` and optional `p`; `text` overrides the wording. */
export function ForecastChip({ f, showPct = true, perspective, text, className, ...rest }: {
  f?: Forecast; showPct?: boolean; label?: ForecastLabel; perspective: Perspective; p?: number; text?: string; className?: string;
}) {
  const label = rest.label ?? f?.label ?? "tossup";
  const p = rest.p ?? (f && showPct ? f.p : undefined);
  const Icon = LABEL_ICON[label];
  return (
    <span className={`forecast-chip forecast-chip--${label}${className ? ` ${className}` : ""}`}>
      <Icon size={12} aria-hidden />
      <span className="forecast-chip__text" key={text ?? chipText(label, perspective)}>{text ?? `${chipText(label, perspective)}${p !== undefined ? ` · ${pct(p)}%` : ""}`}</span>
    </span>
  );
}

/** Motion 11: roll old → new when the value changes after first paint. */
export function Roll({ value, className }: { value: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const prev = useRef(value);
  useLayoutEffect(() => {
    if (prev.current === value) return;
    prev.current = value;
    if (!ref.current || prefersReducedMotion()) return;
    ref.current.animate([{ transform: "translateY(60%)", opacity: 0 }, { transform: "none", opacity: 1 }],
      { duration: recipes.liveValueChange.duration, easing: easing.standard });
  }, [value]);
  return <span ref={ref} className={`live-number${className ? ` ${className}` : ""}`}>{value}</span>;
}

/** Track 120 × 12: rail, 50% midline, range band (30%), 4px marker. Positions transition (L2–L4). */
export function ForecastMeter({ f, perspective, showValue = true, width = 120 }: { f: Forecast; perspective: Perspective; showValue?: boolean; width?: number }) {
  const style = width === 120 ? undefined : ({ ["--w" as string]: `${width}px` } as CSSProperties);
  return (
    <span className={`forecast-meter forecast-meter--${f.label}`} style={style} role="img"
      aria-label={`Forecast: ${pct(f.p)}% ${way(perspective)}, range ${pct(f.low)} to ${pct(f.high)}%, ${f.confidence} confidence`}>
      <span className="forecast-meter__track" aria-hidden>
        <span className="forecast-meter__range" style={{ left: `${f.low * 100}%`, width: `${(f.high - f.low) * 100}%` }} />
        <span className="forecast-meter__marker" style={{ left: `${f.p * 100}%` }} />
      </span>
      {showValue && <Roll className="forecast-meter__value" value={`${pct(f.p)}%`} />}
    </span>
  );
}

export const FACTOR_NAMES: Record<ForecastFactor["id"], string> = {
  moneyLinked: "Money-linked votes with this client",
  leaning: "Leaning vs. the bill",
  similar: "Record on similar items",
  tension: "Council tension",
  // Learned factors (forecast/model.ts): shown only when the evidence exists. Copy pending designer review.
  sponsor: "Sponsor of this item",
  committee: "Committee action",
  said: "Said on the record",
  mayor: "Mayor-sponsored item",
  allies: "Allies' positions",
  wardVoice: "Ward residents' voice",
  moneyAmount: "Money from clients with a stated position",
  graph: "Relationship graph",
  homeWard: "Their own ward",
  procedure: "How the item has moved",
  wardElectorate: "The ward's electorate",
  wardDemographics: "The ward's residents",
  mayorStatement: "What the Mayor said",
  leaning2: "Second vote axis",
};

const signed = (x: number) => (Math.abs(x) < 0.005 ? "0.00" : `${x >= 0 ? "+" : "−"}${Math.abs(x).toFixed(2)}`);
const confidence = (c: Forecast["confidence"]) => c[0].toUpperCase() + c.slice(1);
const LABEL_WORD: Record<ForecastLabel, string> = { likely: "Likely", tossup: "Toss-up", unlikely: "Unlikely" };

/**
 * "Why 66% their way" (L1): signed bars in log-odds from the zero line (45px per unit, half-track 58px).
 * Bars grow from zero one after another, biggest first. `details` are the sentences from the API.
 */
export function ForecastWhy({ f, perspective, details, topic, inferred, children }: {
  f: Forecast; perspective: Perspective; details: Record<ForecastFactor["id"], string>; topic?: string;
  /** The client's position was inferred, not stated in a filing: always say so (design 26). */
  inferred?: boolean; children?: React.ReactNode;
}) {
  const order = [...f.factors].sort((a, b) => Math.abs(b.effect) - Math.abs(a.effect)).map((x) => x.id);
  const bars = recipes.whyExpand.bars;
  return (
    <section className="forecast-why" aria-label="Why this forecast">
      <header className="forecast-why__head">
        <strong>Why {pct(f.p)}% {way(perspective)}</strong>
        <span>Range {pct(f.low)}–{pct(f.high)}% · {confidence(f.confidence)} confidence · {inferred ? "position inferred" : LABEL_WORD[f.label]}</span>
      </header>
      {f.factors.map((x) => {
        const w = Math.min(50, Math.max(2, (Math.abs(x.effect) / 1.3) * 50));
        const delay = order.indexOf(x.id) * bars.stagger;
        return (
          <div className="forecast-factor" key={x.id}>
            <div className="forecast-factor__text">
              <b>{x.id === "tension" && topic ? `Council tension on ${topic}` : FACTOR_NAMES[x.id]}</b>
              <span>{details[x.id]}</span>
            </div>
            <span className="forecast-factor__bar" aria-hidden>
              <span className={x.effect >= 0 ? "is-for" : "is-against"}
                style={{ ...(x.effect >= 0 ? { left: "50%" } : { right: "50%" }), width: `${w}%`, animationDelay: `${delay}ms`, animationDuration: `${bars.duration}ms` }} />
            </span>
            <span className="forecast-factor__effect">{signed(x.effect)}</span>
          </div>
        );
      })}
      {children}
    </section>
  );
}

export function TensionGauge({ t, label, strong }: { t: number; label?: string; strong?: boolean }) {
  const on = Math.max(1, Math.round(t * 5));
  return (
    <span className="tension" role="img" aria-label={`${tensionLabel(t)} council tension, ${t.toFixed(2)}`}>
      {[0, 1, 2, 3, 4].map((i) => <span key={i} aria-hidden className={i < on ? "tension__seg is-on" : "tension__seg"} />)}
      <span className={strong ? "tension__label tension__label--strong" : "tension__label"}>{label ?? `${tensionLabel(t)} · ${t.toFixed(2)}`}</span>
    </span>
  );
}

export type Group = ForecastLabel;

/** Likely yes / Toss-up / Likely no. L5: hovering a segment dims the others; click to filter. */
export function ProjectionBar({ likelyYes, tossup, likelyNo, labels = ["Likely yes", "Toss-up", "Likely no"], hover, onHover, onSelect }: {
  likelyYes: number; tossup: number; likelyNo: number;
  labels?: [string, string, string];
  hover?: Group | null;
  onHover?: (g: Group | null) => void;
  onSelect?: (g: Group) => void;
}) {
  const segs = [["likely", "is-yes", likelyYes, labels[0]], ["tossup", "is-tossup", tossup, labels[1]], ["unlikely", "is-no", likelyNo, labels[2]]] as const;
  const interactive = !!onHover || !!onSelect;
  return (
    <div className="projection" data-hover={hover ?? undefined} onPointerLeave={() => onHover?.(null)}>
      <div className="projection__bar" role={interactive ? "group" : "img"} aria-label={`${labels[0]} ${likelyYes}, ${labels[1]} ${tossup}, ${labels[2]} ${likelyNo}`}>
        {segs.map(([g, cls, n, l]) => n > 0 && (interactive ? (
          <span key={g} className={cls} data-group={g} style={{ flexGrow: n }} role="button" tabIndex={0} aria-label={`${l} ${n}`}
            onPointerEnter={() => onHover?.(g)} onFocus={() => onHover?.(g)} onBlur={() => onHover?.(null)} onClick={() => onSelect?.(g)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect?.(g); } }} />
        ) : <span key={g} className={cls} data-group={g} style={{ flexGrow: n }} />))}
      </div>
      <div className="projection__legend"><span>{labels[0]} {likelyYes}</span><span>{labels[1]} {tossup}</span><span>{labels[2]} {likelyNo}</span></div>
    </div>
  );
}
